/**
 * `production.*` wire contract (garment Increment 6, `garment.production`).
 *
 * Daily per-line/stage output capture + the Style File's progress read-model.
 * Quantities are plain integer pieces (`qty` is signed — negative = correction).
 * Behind `entitledProcedureFor("garment.production")`.
 */
import { z } from "zod";
import { uuid, isoDate } from "../_common.js";

export const productionStage = z.enum(["cut", "sew", "finish", "pack"]);
const pieces = z.number().int().min(-1_000_000).max(1_000_000);

export const getForStyleInput = z.object({ styleId: uuid });

export const recordInput = z.object({
  styleId: uuid,
  stage: productionStage,
  entryDate: isoDate,
  qty: pieces,
  lineRef: z.string().trim().max(40).nullable().optional(),
  operatorsPresent: z.number().int().min(0).max(100_000).nullable().optional(),
  operatorsRoster: z.number().int().min(0).max(100_000).nullable().optional(),
  delayReason: z.string().trim().max(80).nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
});
export const recordOutput = z.object({ id: uuid, styleStatus: z.string() });

export const productionView = z.object({
  styleId: uuid,
  status: z.string(),
  orderQty: z.number().int().nonnegative(),
  canRecord: z.boolean(),
  stages: z.array(
    z.object({
      stage: productionStage,
      label: z.string(),
      cumulativeQty: z.number().int(),
      orderQty: z.number().int().nonnegative(),
      percent: z.number().int().min(0).max(100),
    }),
  ),
  entries: z.array(
    z.object({
      id: uuid,
      stage: productionStage,
      entryDate: isoDate,
      qty: z.number().int(),
      lineRef: z.string().nullable(),
      operatorsPresent: z.number().int().nullable(),
      operatorsRoster: z.number().int().nullable(),
      delayReason: z.string().nullable(),
      notes: z.string().nullable(),
      createdAt: z.string().datetime(),
    }),
  ),
  lastEntryDate: isoDate.nullable(),
  daysSinceLastEntry: z.number().int().nullable(),
  behindPlan: z.boolean(),
});
export const getForStyleOutput = productionView;

export type ProductionStage = z.infer<typeof productionStage>;
export type ProductionViewWire = z.infer<typeof productionView>;
